import { Link } from "react-router-dom";
import { LaxOrnament } from "@/components/Ornaments";

const COL_SIDOR = [
  { to: "/", label: "Start" },
  { to: "/anslagstavla", label: "Anslagstavla" },
  { to: "/foreningar-evenemang", label: "Föreningar" },
  { to: "/foretag", label: "Företag" },
  { to: "/bostader", label: "Bostäder" },
  { to: "/praktiskt", label: "Praktiskt" },
  { to: "/friluftsliv", label: "Friluftsliv" },
  { to: "/kronika", label: "Kronika" },
  { to: "/bilder", label: "Ditt foto" },
  { to: "/karta", label: "Karta" },
  { to: "/om-oss", label: "Om oss" },
];

const COL_DELTA = [
  { to: "/skicka-anslag", label: "Skicka in anslag" },
  { to: "/skicka-evenemang", label: "Tipsa om evenemang" },
  { to: "/tipsa", label: "Tipsa från Facebook" },
  { to: "/veckans-handelser", label: "Veckans händelser" },
  { to: "/bilder/skicka", label: "Skicka in foto" },
  { to: "/ny-i-bygden", label: "Ny i bygden?" },
  { to: "/kontakt", label: "Kontakta redaktionen" },
  { to: "/om-oss#namntavling", label: "Namntävling" },
  { to: "/om-oss#moderator-section", label: "Bli medmoderator" },
];

const COL_OM = [
  { to: "/om-oss", label: "Om oss" },
  { to: "/riktlinjer", label: "Riktlinjer" },
  { to: "/jamtlandssangen", label: "Jämtlandssången" },
  { to: "/om-oss#tillganglighet-section", label: "Tillgänglighet" },
];

const COL_LEGAL = [
  { to: "/integritetspolicy", label: "Integritetspolicy" },
  { to: "/anvandarvillkor", label: "Användarvillkor" },
  { to: "/villkor-inskickat", label: "Villkor för inskickat material" },
  { to: "/cookiepolicy", label: "Cookiepolicy" },
  { to: "/ansvarig-utgivare", label: "Ansvarig utgivare" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-ink mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <p className="font-heading text-xl font-bold text-parchment mb-1 tracking-tight" translate="no">
              <span className="notranslate">vidalv</span><span className="text-parchment/30 notranslate">.live</span>
            </p>
            <p className="text-sm text-parchment/40 leading-relaxed mb-3" translate="no">
              <span className="notranslate">Backe, Rossön och bygderna &ndash; .live vid Fjällsjöälven.</span>
            </p>
            <div className="mb-3 flex items-center gap-2">
              <LaxOrnament className="w-20 text-angermanland-silver opacity-60" />
              <span className="text-[9px] uppercase tracking-[0.22em] text-parchment/35 font-body">
                Ångermanland · Tre silverlaxar
              </span>
            </div>
            <p className="text-xs text-parchment/30 leading-relaxed">
              Drivs lokalt av <a href="https://www.hodema.se" target="_blank" rel="noopener noreferrer" className="text-parchment/50 hover:text-parchment no-underline underline-offset-2 hover:underline">hodema edertools ab</a> i Backe. Allt som publiceras granskas.
            </p>
            {/* Positionierungs-/Copy-Fix — ruhige Zeile, kein Badge, kein Techsprache */}
            <p data-testid="footer-claim" className="text-xs text-parchment/30 leading-relaxed mt-2">
              <span translate="no" className="notranslate">vidalv.live</span> – lokal information som kan uppdateras, följas upp och förbättras tillsammans.
            </p>
            <a
              href="mailto:redaktion@vidalv.live"
              data-testid="footer-email"
              className="inline-block mt-3 text-sm text-parchment/60 hover:text-parchment no-underline transition-colors"
            >
              redaktion@vidalv.live
            </a>
          </div>

          {/* Sidor */}
          <FooterColumn title="Sidor" items={COL_SIDOR} testidPrefix="footer-sidor" />

          {/* Delta */}
          <FooterColumn title="Delta" items={COL_DELTA} testidPrefix="footer-delta" />

          {/* Om vidalv.live */}
          <FooterColumn title="Om vidalv.live" items={COL_OM} testidPrefix="footer-om" />

          {/* Rättsligt */}
          <FooterColumn title="Rättsligt" items={COL_LEGAL} testidPrefix="footer-legal" />
        </div>

        {/* Norrland-motto */}
        <div data-testid="footer-norrland-motto" className="mt-12 pt-8 pb-2 border-t border-parchment/10 text-center">
          <p className="font-fraktur-smooth text-gold-deep text-lg sm:text-xl lg:text-2xl leading-snug tracking-wide max-w-xl mx-auto">
            &bdquo;I Norrland lever man inte av stora inkomster <br className="hidden sm:block" />&mdash; utan av små utgifter.&rdquo;
          </p>
          <p className="mt-3 text-[10px] font-body uppercase tracking-[0.28em] text-parchment/35">
            &mdash; Norrländskt ordspråk &mdash;
          </p>
        </div>

        {/* Bottom row */}
        <div className="mt-12 pt-6 border-t border-parchment/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-parchment/30" translate="no">
            © {new Date().getFullYear()} <span className="notranslate">vidalv.live</span> &middot; Innehåll under granskning av lokal redaktion.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.hodema.se"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-hodema"
              className="text-xs text-parchment/30 hover:text-parchment/60 no-underline transition-colors"
            >
              www.hodema.se
            </a>
            <Link
              to="/mod/login"
              data-testid="footer-mod-login"
              className="text-xs text-parchment/20 hover:text-parchment/50 no-underline transition-colors"
            >
              Moderator
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, items, testidPrefix }) {
  const slugify = (s) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div>
      <p className="font-body text-xs font-semibold text-parchment/25 uppercase tracking-[0.2em] mb-4">
        {title}
      </p>
      <nav className="flex flex-col gap-2.5">
        {items.map((item) => {
          const slug = slugify(item.label);
          const isHash = item.to.includes("#");
          return isHash ? (
            <a
              key={item.to}
              href={item.to}
              data-testid={`${testidPrefix}-${slug}`}
              className="text-sm text-parchment/50 hover:text-parchment no-underline transition-colors"
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`${testidPrefix}-${slug}`}
              className="text-sm text-parchment/50 hover:text-parchment no-underline transition-colors"
            >
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
